/** GameSpec — a composed game as data only: archetypes (render kinds) built
 * from registered modules with configs, global modules, players, HUD, input
 * and presentation hints. A template is a frozen GameSpec plus bounded
 * tunables; the generation process writes or patches GameSpecs, never code. */
import { assertConfigSchema } from '../schema.js';
import { cloneJson, type JsonObject } from '../json.js';
import { ANIM_STATE_NAMES, type AnimStateName } from '../animStates.js';
import { archetypeRefsOf, type AnyComposeModule, type ModuleValidationContextV1 } from './module.js';

export const GAME_SPEC_SCHEMA = 'game-spec@1';
export const COMPOSE_KIT = 'compose@1';

/** Closed input profiles: semantic seat input → the generic command
 * `{dx, dy, a, b, p}`. The profile decides touch layout and key verbs.
 * `pointer` aims with the pointer (`p`, pressing is `a`): the board is the
 * touch surface, and keys steer the same cursor (arrows) and press (Space). */
export const INPUT_PROFILES = ['run-jump', 'run-jump-action', 'move4-fire', 'move4-fire-action', 'lane-fire', 'pointer'] as const;
export type InputProfileV1 = typeof INPUT_PROFILES[number];

/** The command channels each profile drives beyond `dx` and `a` — the ONE
 * table the solver, the verb-proof planner and the native proof read:
 * `dy` (a vertical axis: climb, steer up/down) and `b` (a second button). */
export const INPUT_PROFILE_CHANNELS: Readonly<Record<InputProfileV1, { dy: boolean; b: boolean }>> = Object.freeze({
  'run-jump': { dy: false, b: false },
  'run-jump-action': { dy: false, b: true },
  'move4-fire': { dy: true, b: false },
  'move4-fire-action': { dy: true, b: true },
  'lane-fire': { dy: false, b: false },
  pointer: { dy: false, b: false },
});

export const PROJECTIONS = ['side', 'top-down', 'front'] as const;
export type ProjectionV1 = typeof PROJECTIONS[number];

export interface ModuleUseV1 {
  use: string;
  /** Global instance key; defaults to the module id. Unique per spec. */
  key?: string;
  config: JsonObject;
}

export interface ArchetypeRenderV1 {
  /** Index into `presentation.palette` for the coded fallback. */
  palette: number;
  depth: number;
  anchor: 'bottom' | 'center';
  shadow?: boolean;
  /** Collision surface drawn as opaque geometry with art as a skin. */
  surface?: boolean;
  /** Recoloured per instance; its art is generated neutral. */
  neutral?: boolean;
  /** How the kind's art faces: `side` (drawn facing right, mirrored for
   * left — a walking/flying actor), `front` (faces the viewer — a block,
   * prop or pickup), `symmetric` (no facing) or `diagonal` (a three-quarter
   * view whose nose points up-right along a diagonal scroll, never mirrored —
   * every craft in a pseudo-isometric flight game). Absent: the platform's
   * default (an animated kind faces its motion, a stateless one the viewer). */
  facing?: 'side' | 'front' | 'symmetric' | 'diagonal';
  /** The one short sign the kind's art carries verbatim ("POW") — there is
   * no runtime text-on-entity layer for a kind whose identity IS its sign.
   * Its exact alphabet is the platform's sprite-label rule. */
  label?: string;
  /** The kind whose ACCEPTED art this kind's art is derived from, recoloured
   * from the source's palette colour to this kind's own (a versus rival in
   * the other seat's colours): it costs no generation and is never judged —
   * its source was. The source is another, non-derived kind of the same game
   * with the same size and at least this kind's states, on a different
   * palette slot; neither kind is neutral and both palette colours carry a hue
   * ({@link DERIVED_RECOLOR_V1}). */
  derivedFrom?: string;
}

/** The ONE recolour contract of a derived kind's art: pixels whose hue lies
 * within `hueBandDegrees` of the SOURCE palette colour's hue, with saturation
 * and value at least the floors, move by the hue difference to the target
 * colour; alpha, saturation, value and every other pixel (skin, outline,
 * highlights) are unchanged. Both palette colours must clear the saturation
 * floor, or there is no hue to move from or to. Sized on a production
 * composed mario sprite: a 12° band and a 0.5 saturation floor move the cap
 * and shirt and leave the pale-pink skin highlights (≈10–15°, saturation
 * 0.3–0.5) and the brown moustache untouched; 18° / 0.25 speckled the face.
 *
 * `minMovedFraction`: a recolour that moves fewer of the source image's
 * opaque pixels than this did not recolour the subject — the source does not
 * wear its palette colour — and the derived slot would ship as a twin of its
 * source. Its source slot is then not accepted (regenerated, or deferred with
 * its derived slots), never published beside a twin. Measured with this band:
 * two production composed mario images wearing the palette red moved 11.6 %
 * (sprite, 2,349 of 20,180 opaque pixels) and 11.2 % (idle strip, 4,841 of
 * 43,322); the grey-blue plumber of the live "Plumber Duel (Composed 2)"
 * moved 1.8 % (sprite, 369 of 20,829) and 0.6 % (idle strip, 267 of 45,645),
 * and its rival read identical. 5 % sits by the geometric mean of the two
 * groups (≈ 4.5 %), at least 2× clear of each. */
export const DERIVED_RECOLOR_V1 = Object.freeze({ hueBandDegrees: 12, minSaturation: 0.5, minValue: 0.12, minMovedFraction: 0.05 });

/** A `#rrggbb` colour's HSV saturation, in [0, 1]. */
export function hexSaturation(hex: string): number {
  const n = Number.parseInt(hex.slice(1), 16);
  const channels = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
  const max = Math.max(...channels), min = Math.min(...channels);
  return max === 0 ? 0 : (max - min) / max;
}

/** A `#rrggbb` colour's HSV hue in degrees, [0, 360) (0 for a grey). The
 * hue {@link DERIVED_RECOLOR_V1}'s band centres on. */
export function hexHue(hex: string): number {
  const n = Number.parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  const max = Math.max(r, g, b), delta = max - Math.min(r, g, b);
  if (delta === 0) return 0;
  const hue = max === r ? 60 * (((g - b) / delta) % 6) : max === g ? 60 * ((b - r) / delta) + 120 : 60 * ((r - g) / delta) + 240;
  return (hue + 360) % 360;
}

/** The plain colour word for a `#rrggbb` colour's hue (red, orange, yellow,
 * green, cyan, blue, purple, magenta) — what an image prompt names beside the
 * exact hex. */
export function hexHueName(hex: string): string {
  const hue = hexHue(hex);
  const names: Array<[number, string]> = [[15, 'red'], [45, 'orange'], [70, 'yellow'], [165, 'green'], [195, 'cyan'],
    [255, 'blue'], [290, 'purple'], [345, 'magenta'], [360, 'red']];
  return names.find(([limit]) => hue < limit)![1];
}

export interface ArchetypeSpecV1 {
  /** The kind's SUBJECT line: what its one sprite draws, on its own — shape,
   * colours and parts, never its situation in play (what holds, carries or
   * captures it, what it rides, where it sits relative to another object):
   * the modules stage that, and the template's disclosed mechanics say it.
   * It feeds the art recommender, the default art direction and the review;
   * `@djgg/creations` `subjectSituationsNamedV1` is the check every template
   * role and every patched one passes. */
  role: string;
  tags: string[];
  size: { w: number; h: number };
  /** Spawn height above the ground plane (an aircraft's cruising layer);
   * absent ⇒ on the ground (0). */
  elevation?: number;
  render: ArchetypeRenderV1;
  modules: ModuleUseV1[];
}

export interface HudRefV1 {
  /** Global module key, archetype module id, or `score`. */
  from: string;
  field: string;
  /** The field's caption in this game's words (`STRIKES` for a lives
   * field), replacing the module's generic one. */
  label?: string;
}

/** A FOLLOW camera: the screen shows a `view`-sized window onto a larger
 * world (a descent column, a tall shaft) that follows the viewing seat's
 * player; the platform eases it (`ease` of the gap per tick, in (0, 1]),
 * leads it `lookAhead` units along the player's facing, frames the player
 * `bias` off the view's centre (positive y shows more below — a descent sees
 * the ground coming) and clamps it to the world. Modules keep simulating in
 * world coordinates. */
export interface CameraFollowV1 {
  mode: 'follow';
  view: { w: number; h: number };
  lookAhead: number;
  ease: number;
  bias: { x: number; y: number };
}

/** An AUTO-SCROLL camera: the world IS the screen (positions stay
 * view-space) and the view travels over the ground on its own — driven by
 * the spec's one global module that publishes `view()` (a strike route's
 * scroll track), whose offset scrolls the ground art. */
export interface CameraAutoScrollV1 {
  mode: 'auto-scroll';
}

/** How the screen frames the world. Absent from a spec: `fixed` — the whole
 * world is one screen. */
export type CameraSpecV1 = CameraFollowV1 | CameraAutoScrollV1;
/** The ONE camera model (`fixed | follow | auto-scroll`) the presentation
 * carries and the render manifest and composed adapter consume. */
export type ComposedCameraV1 = { mode: 'fixed' } | CameraSpecV1;

export interface GameSpecV1 {
  schema: typeof GAME_SPEC_SCHEMA;
  kit: typeof COMPOSE_KIT;
  title: string;
  /** Genre phrase for generated art ("a single-screen platform arcade game"). */
  summary: string;
  /** The simulated world. Its orientation is the SCREEN's: the view's aspect
   * under a follow camera, else the world's own. */
  world: { w: number; h: number; orientation: 'landscape' | 'portrait' };
  /** Absent: a fixed single screen. */
  camera?: CameraSpecV1;
  players: {
    /** Player archetype per seat (index == slot). */
    archetypes: string[];
    spawns: Array<{ x: number; y: number; facing: 1 | -1 }>;
    /** How the seats of a multi-seat game relate: `coop` (one team wins or
     * loses together; seat 2 may stay empty) or `versus` (a match between
     * the seats, decided by a versus objective). Exactly the multi-seat
     * specs declare it. */
    relation?: SeatRelationV1;
  };
  archetypes: Record<string, ArchetypeSpecV1>;
  globals: ModuleUseV1[];
  hud: HudRefV1[];
  input: { profile: InputProfileV1; labels: { a: string; b?: string } };
  presentation: {
    projection: ProjectionV1;
    backgroundRole: string;
    palette: string[];
    backgroundPaletteIndex: number;
  };
}

export type SeatRelationV1 = 'coop' | 'versus';
/** `flow.objective` win rules that decide a match between seats. */
export const VERSUS_OBJECTIVES = ['last-standing', 'first-to', 'most-at-time'] as const;

/** A spec's seat relation: `solo` for one seat. */
export function seatRelation(spec: Pick<GameSpecV1, 'players'>): 'solo' | SeatRelationV1 {
  return spec.players.archetypes.length === 1 ? 'solo' : spec.players.relation!;
}

/** The seats a match of this spec plays with: every seat of a versus match
 * (empty ones are bots), one for everything else (a co-op game's second
 * seat stays empty unless a second player joins). */
export function matchSeats(spec: Pick<GameSpecV1, 'players'>): number {
  return seatRelation(spec) === 'versus' ? spec.players.archetypes.length : 1;
}

export type SpecValidationV1 =
  | { ok: true; spec: GameSpecV1 }
  | { ok: false; errors: string[] };

export const COMPOSE_LIMITS = Object.freeze({ archetypes: 16, modulesPerArchetype: 12, globals: 24,
  hud: 8, palette: 16, worldMin: 4, worldMax: 64, players: 2 });

const KIND = /^[a-z][a-z0-9-]{0,39}$/;
const TAG = /^[a-z][a-z0-9-]{0,23}$/;
const HEX = /^#[0-9a-f]{6}$/;

function text(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= max;
}
function finite(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
}

/** The archetype's states, derived from its modules (+ global tag grants). */
export function archetypeAnimStates(spec: GameSpecV1, archetype: string,
  registry: ReadonlyMap<string, AnyComposeModule>): AnimStateName[] {
  const a = spec.archetypes[archetype]!;
  const states = new Set<AnimStateName>();
  for (const use of a.modules) for (const s of registry.get(use.use)!.animStates(use.config)) states.add(s);
  for (const use of spec.globals) {
    const grants = registry.get(use.use)!.animStatesForTag?.(use.config) ?? [];
    for (const grant of grants) if (a.tags.includes(grant.tag)) for (const s of grant.states) states.add(s);
  }
  return ANIM_STATE_NAMES.filter(s => states.has(s));
}

/** Structural + semantic validation. Collects every error (a repairing
 * generator gets the whole list at once). Returns a detached clone. */
export function validateGameSpec(input: unknown, registry: ReadonlyMap<string, AnyComposeModule>): SpecValidationV1 {
  const errors: string[] = [];
  const fail = (path: string, message: string) => { errors.push(`${path}: ${message}`); };
  let spec: GameSpecV1;
  try {
    // Untrusted input: detached JSON, then every field below is checked.
    const parsed: unknown = cloneJson(input);
    spec = parsed as GameSpecV1;
  } catch (e) {
    return { ok: false, errors: [`$: ${(e as Error).message}`] };
  }
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return { ok: false, errors: ['$: expected object'] };
  const allowed = ['schema', 'kit', 'title', 'summary', 'world', 'camera', 'players', 'archetypes', 'globals', 'hud', 'input', 'presentation'];
  for (const key of Object.keys(spec)) if (!allowed.includes(key)) fail(key, 'unsupported field');
  if (spec.schema !== GAME_SPEC_SCHEMA) fail('schema', `expected ${GAME_SPEC_SCHEMA}`);
  if (spec.kit !== COMPOSE_KIT) fail('kit', `expected ${COMPOSE_KIT}`);
  if (!text(spec.title, 80)) fail('title', 'expected 1..80 chars');
  if (!text(spec.summary, 160)) fail('summary', 'expected 1..160 chars');
  const world = spec.world;
  if (!world || !finite(world.w, COMPOSE_LIMITS.worldMin, COMPOSE_LIMITS.worldMax) || !finite(world.h, COMPOSE_LIMITS.worldMin, COMPOSE_LIMITS.worldMax)
    || !['landscape', 'portrait'].includes(world.orientation)) {
    fail('world', 'expected {w,h} in world bounds and orientation landscape|portrait');
    return { ok: false, errors };
  }
  const camera = spec.camera;
  const follow = camera?.mode === 'follow';
  if (camera !== undefined) {
    if (!camera || typeof camera !== 'object' || Array.isArray(camera) || !['follow', 'auto-scroll'].includes(camera.mode)) {
      fail('camera', 'expected {mode: follow|auto-scroll, …} (absent: a fixed screen)');
    } else if (camera.mode === 'auto-scroll') {
      if (Object.keys(camera).some(k => k !== 'mode')) fail('camera', 'an auto-scroll camera declares only its mode (its global view module drives it)');
    } else if (Object.keys(camera).some(k => !['mode', 'view', 'lookAhead', 'ease', 'bias'].includes(k))
      || !camera.view || typeof camera.view !== 'object' || Object.keys(camera.view).some(k => !['w', 'h'].includes(k))
      || !finite(camera.view.w, COMPOSE_LIMITS.worldMin, world.w) || !finite(camera.view.h, COMPOSE_LIMITS.worldMin, world.h)
      || !finite(camera.lookAhead, 0, 8) || !finite(camera.ease, 0.01, 1)
      || !camera.bias || typeof camera.bias !== 'object' || Object.keys(camera.bias).some(k => !['x', 'y'].includes(k))
      || !finite(camera.bias.x, -camera.view.w / 2, camera.view.w / 2) || !finite(camera.bias.y, -camera.view.h / 2, camera.view.h / 2)) {
      fail('camera', 'expected a follow camera {mode, view: {w, h} within the world, lookAhead 0..8, ease 0.01..1, bias {x, y} within half the view}');
    } else if (camera.view.w === world.w && camera.view.h === world.h) fail('camera.view', 'a follow camera views less than the whole world');
  }
  const screen = follow && camera.view && finite(camera.view.w, 0, Infinity) && finite(camera.view.h, 0, Infinity) ? camera.view : world;
  if ((world.orientation === 'landscape') !== (screen.w >= screen.h)) fail('world.orientation', `must match the ${follow ? 'view' : 'world'} aspect`);

  // Presentation.
  const p = spec.presentation;
  if (!p) fail('presentation', 'expected { projection, backgroundRole, palette, backgroundPaletteIndex }');
  else {
    if (!PROJECTIONS.includes(p.projection)) fail('presentation.projection', `expected one of ${PROJECTIONS.join(', ')}`);
    if (!text(p.backgroundRole, 120)) fail('presentation.backgroundRole', 'expected text of at most 120 characters');
    const paletteOk = Array.isArray(p.palette) && p.palette.length >= 2 && p.palette.length <= COMPOSE_LIMITS.palette
      && p.palette.every(c => typeof c === 'string' && HEX.test(c));
    if (!paletteOk) fail('presentation.palette', `expected 2..${COMPOSE_LIMITS.palette} lowercase #rrggbb colours`);
    if (!Number.isSafeInteger(p.backgroundPaletteIndex) || p.backgroundPaletteIndex < 0
      || (paletteOk && p.backgroundPaletteIndex >= p.palette.length)) fail('presentation.backgroundPaletteIndex', 'expected an index into the palette');
  }
  const paletteSize = Array.isArray(p?.palette) ? p.palette.length : 0;

  // Archetypes and their modules.
  const names = spec.archetypes && typeof spec.archetypes === 'object' && !Array.isArray(spec.archetypes) ? Object.keys(spec.archetypes) : [];
  if (names.length < 1 || names.length > COMPOSE_LIMITS.archetypes) fail('archetypes', `expected 1..${COMPOSE_LIMITS.archetypes}`);
  const contextArchetypes: Record<string, { tags: readonly string[]; modules: readonly string[] }> = {};
  const contextGlobals: string[] = [];
  const context: ModuleValidationContextV1 = { archetypes: contextArchetypes, globals: contextGlobals, world: { w: world.w, h: world.h }, paletteSize };
  const present = new Set<string>();
  const shaped = new Set<ModuleUseV1>();
  for (const name of names) {
    const a = spec.archetypes[name]!;
    const path = `archetypes.${name}`;
    if (!KIND.test(name) || name === 'background') { fail(path, 'archetype names are kebab-case kinds (never "background")'); continue; }
    if (!a || typeof a !== 'object') { fail(path, 'expected object'); continue; }
    for (const key of Object.keys(a)) if (!['role', 'tags', 'size', 'elevation', 'render', 'modules'].includes(key)) fail(`${path}.${key}`, 'unsupported field');
    if (a.elevation !== undefined && !finite(a.elevation, 0, 8)) fail(`${path}.elevation`, 'expected 0..8');
    if (!text(a.role, 120)) fail(`${path}.role`, 'expected 1..120 chars');
    if (!Array.isArray(a.tags) || a.tags.length > 8 || a.tags.some(t => typeof t !== 'string' || !TAG.test(t)) || new Set(a.tags).size !== a.tags.length) {
      fail(`${path}.tags`, 'expected ≤8 unique kebab-case tags');
    }
    if (!a.size || !finite(a.size.w, 0.05, world.w) || !finite(a.size.h, 0.05, world.h)) fail(`${path}.size`, 'expected positive size within the world');
    const r = a.render;
    if (!r || !Number.isSafeInteger(r.palette) || r.palette < 0 || r.palette >= paletteSize || !finite(r.depth, -100, 100)
      || !['bottom', 'center'].includes(r.anchor)) fail(`${path}.render`, 'expected palette index, depth and anchor bottom|center');
    if (r && r.facing !== undefined && !['side', 'front', 'symmetric', 'diagonal'].includes(r.facing)) fail(`${path}.render.facing`, 'expected side|front|symmetric|diagonal');
    if (r && r.label !== undefined && !text(r.label, 12)) fail(`${path}.render.label`, 'expected 1..12 chars');
    if (r && r.derivedFrom !== undefined && (typeof r.derivedFrom !== 'string' || !KIND.test(r.derivedFrom))) fail(`${path}.render.derivedFrom`, 'expected a kind name');
    if (!Array.isArray(a.modules) || a.modules.length < 1 || a.modules.length > COMPOSE_LIMITS.modulesPerArchetype) {
      fail(`${path}.modules`, `expected 1..${COMPOSE_LIMITS.modulesPerArchetype}`); continue;
    }
    const ids: string[] = [];
    a.modules.forEach((use, i) => {
      const mod = resolveUse(use, `${path}.modules[${i}]`, 'entity');
      if (!mod) return;
      if (ids.includes(mod.id)) fail(`${path}.modules[${i}]`, `module ${mod.id} attached twice`);
      ids.push(mod.id);
    });
    const movement = a.modules.filter(use => registry.get(use.use)?.role === 'movement');
    if (movement.length !== 1) fail(`${path}.modules`, `exactly one movement module is required (found ${movement.length})`);
    contextArchetypes[name] = { tags: Array.isArray(a.tags) ? a.tags : [], modules: ids };
  }

  // Derived kinds: the art is the source's, recoloured (DERIVED_RECOLOR_V1).
  for (const name of names) {
    const a = spec.archetypes[name], source = a?.render?.derivedFrom;
    if (typeof source !== 'string') continue;
    const path = `archetypes.${name}.render.derivedFrom`;
    const s = spec.archetypes[source];
    if (source === name || !s || typeof s !== 'object' || !names.includes(source)) { fail(path, `derives from unknown kind ${source}`); continue; }
    if (s.render?.derivedFrom !== undefined) fail(path, `${source} is itself derived; derive from its source`);
    if (!a.size || !s.size || a.size.w !== s.size.w || a.size.h !== s.size.h) fail(path, `a derived kind has its source ${source}'s size`);
    if (a.render.palette === s.render?.palette) fail(path, `a derived kind is recoloured to a different palette slot than ${source}'s`);
    if (a.render.neutral || s.render?.neutral) fail(path, 'a neutral kind (recoloured per instance) neither derives nor is derived from');
    const colours = [a.render.palette, s.render?.palette].map(i => Number.isSafeInteger(i) ? p?.palette?.[i as number] : undefined);
    if (colours.some(c => typeof c !== 'string' || !HEX.test(c) || hexSaturation(c) < DERIVED_RECOLOR_V1.minSaturation)) {
      fail(path, `a derived kind and its source ${source} both have a chromatic palette colour (saturation ≥ ${DERIVED_RECOLOR_V1.minSaturation})`);
    }
  }

  // Globals.
  const keys = new Set<string>();
  if (!Array.isArray(spec.globals) || spec.globals.length > COMPOSE_LIMITS.globals) fail('globals', `expected ≤${COMPOSE_LIMITS.globals}`);
  else spec.globals.forEach((use, i) => {
    const mod = resolveUse(use, `globals[${i}]`, 'global');
    if (!mod) return;
    const key = use.key ?? mod.id;
    if (!/^[a-z][a-zA-Z0-9.-]{0,47}$/.test(key) || keys.has(key)) fail(`globals[${i}].key`, `duplicate or invalid instance key ${key}`);
    keys.add(key);
    contextGlobals.push(mod.id);
  });

  function resolveUse(use: ModuleUseV1, path: string, attach: 'entity' | 'global'): AnyComposeModule | undefined {
    if (!use || typeof use !== 'object' || typeof use.use !== 'string') { fail(path, 'expected {use, config}'); return undefined; }
    for (const key of Object.keys(use)) if (!['use', 'key', 'config'].includes(key)) fail(`${path}.${key}`, 'unsupported field');
    const mod = registry.get(use.use);
    if (!mod) { fail(path, `unknown module ${use.use}`); return undefined; }
    if (mod.attach !== attach) { fail(path, `${mod.id} attaches to ${mod.attach}, not ${attach}`); return undefined; }
    if (attach === 'entity' && use.key !== undefined) fail(`${path}.key`, 'entity modules are keyed by module id');
    present.add(mod.id);
    try { assertConfigSchema(use.config, mod.config, `${path}.config`); }
    catch (e) { errors.push((e as Error).message); return undefined; }
    shaped.add(use);
    return mod;
  }

  // Dependency closure, references and semantic checks, on every use whose own
  // shape holds: one reply hears about all of them together, not a layer per call.
  const check = (use: ModuleUseV1, path: string, archetype: string | null) => {
    if (!shaped.has(use)) return;
    const mod = registry.get(use.use)!;
    for (const req of mod.requires) if (!present.has(req)) fail(path, `${mod.id} requires ${req}`);
    for (const ref of archetypeRefsOf(mod, use.config)) if (!names.includes(ref)) fail(path, `references unknown archetype ${ref}`);
    for (const message of mod.check?.(use.config, context, archetype) ?? []) fail(path, message);
  };
  const usesOf = (name: string): ModuleUseV1[] => (Array.isArray(spec.archetypes[name]?.modules) ? spec.archetypes[name].modules : []);
  const globalUses = Array.isArray(spec.globals) ? spec.globals : [];
  for (const name of names) usesOf(name).forEach((use, i) => check(use, `archetypes.${name}.modules[${i}]`, name));
  // A derived kind's strips are its source's: the source shows every state it does.
  for (const name of names) {
    const source = spec.archetypes[name]?.render?.derivedFrom;
    if (typeof source !== 'string' || !spec.archetypes[source] || ![...usesOf(name), ...usesOf(source), ...globalUses].every(use => shaped.has(use))) continue;
    const shown = archetypeAnimStates(spec, source, registry);
    const missing = archetypeAnimStates(spec, name, registry).filter(state => !shown.includes(state));
    if (missing.length) fail(`archetypes.${name}.render.derivedFrom`, `${source} has no ${missing.join(', ')} state to derive from`);
  }
  globalUses.forEach((use, i) => check(use, `globals[${i}]`, null));
  // The auto-scroll camera and its one driving view module come together.
  const views = globalUses.filter(use => registry.get(use?.use)?.view).map(use => use.use);
  if (spec.camera?.mode === 'auto-scroll' && views.length !== 1) {
    fail('globals', `an auto-scroll camera needs exactly one global module that moves the view (found ${views.length})`);
  } else if (spec.camera?.mode !== 'auto-scroll' && views.length) {
    fail('globals', `${views.join(', ')} moves the view, which only an auto-scroll camera allows, and this game's camera is ${spec.camera?.mode ?? 'fixed'}. `
      + 'A new game sets its own camera; no structure op can change a template\'s, so an adaptation must remove this module');
  }

  // Players and input.
  const players = spec.players;
  if (!players || !Array.isArray(players.archetypes) || players.archetypes.length < 1 || players.archetypes.length > COMPOSE_LIMITS.players
    || !Array.isArray(players.spawns) || players.spawns.length !== players.archetypes.length) {
    fail('players', `expected 1..${COMPOSE_LIMITS.players} seat archetypes with one spawn each`);
  } else {
    players.archetypes.forEach((name, slot) => {
      const a = spec.archetypes[name];
      if (!a) { fail(`players.archetypes[${slot}]`, `unknown archetype ${name}`); return; }
      if (!a.tags.includes('player')) fail(`players.archetypes[${slot}]`, `${name} must carry the player tag`);
      const s = players.spawns[slot]!;
      if (!s || !finite(s.x, 0, world.w) || !finite(s.y, 0, world.h) || ![1, -1].includes(s.facing)) fail(`players.spawns[${slot}]`, 'expected {x,y,facing} inside the world');
    });
    for (const name of names) if (spec.archetypes[name]!.tags.includes('player') && !players.archetypes.includes(name)) {
      fail(`archetypes.${name}`, 'a player-tagged archetype must be seated in players.archetypes');
    }
    if (players.archetypes.length > 1 ? players.relation !== 'coop' && players.relation !== 'versus' : players.relation !== undefined) {
      fail('players.relation', players.archetypes.length > 1 ? 'a multi-seat game declares coop or versus' : 'a one-seat game declares no relation');
    }
    // Versus seats are mirror images (a fair match): the same tags, size,
    // render shape and modules with the same configs; only the kind's name,
    // role line, palette slot and spawn may differ.
    if (players.relation === 'versus') {
      const shape = (name: string) => {
        const a = spec.archetypes[name];
        return a ? JSON.stringify({ tags: a.tags, size: a.size, elevation: a.elevation ?? 0, modules: a.modules }) : '';
      };
      const first = shape(players.archetypes[0]!);
      players.archetypes.slice(1).forEach((name, i) => {
        if (shape(name) !== first) fail(`players.archetypes[${i + 1}]`, `versus seats must mirror ${players.archetypes[0]} (same tags, size and modules with the same configs)`);
      });
    }
    const win = globalUses.find(g => g?.use === 'flow.objective')?.config?.win;
    if (typeof win === 'string' && (players.relation === 'versus') !== (VERSUS_OBJECTIVES as readonly string[]).includes(win)) {
      fail('players.relation', players.relation === 'versus'
        ? `a versus game is decided by a versus objective (${VERSUS_OBJECTIVES.join(', ')})` : `objective ${win} decides a versus match; declare relation versus`);
    }
  }
  if (!spec.input || !INPUT_PROFILES.includes(spec.input.profile) || !spec.input.labels || !text(spec.input.labels.a, 12)
    || (spec.input.labels.b !== undefined && !text(spec.input.labels.b, 12))) {
    fail('input', `expected profile ${INPUT_PROFILES.join('|')} and labels {a, b?} ≤12 chars`);
  } else if (INPUT_PROFILE_CHANNELS[spec.input.profile].b !== (spec.input.labels.b !== undefined)) {
    fail('input.labels.b', 'exactly the profiles with a second button label it');
  }

  // HUD references must name declared fields.
  if (!Array.isArray(spec.hud) || spec.hud.length > COMPOSE_LIMITS.hud) fail('hud', `expected ≤${COMPOSE_LIMITS.hud} fields`);
  else spec.hud.forEach((ref, i) => {
    if (!ref || typeof ref !== 'object' || Object.keys(ref).some(k => !['from', 'field', 'label'].includes(k))) { fail(`hud[${i}]`, 'expected {from, field, label?}'); return; }
    if (ref.label !== undefined && (!text(ref.label, 12) || !/^[A-Z0-9 %!?.'-]+$/.test(ref.label))) fail(`hud[${i}].label`, 'expected 1..12 upper-case characters');
    if (ref.from === 'score' && ref.field === 'score') return;
    const use = globalUses.find(g => (g?.key ?? g?.use) === ref.from) ?? names.flatMap(usesOf).find(m => m?.use === ref.from);
    // A use already reported as misshapen has no fields to judge this reference against.
    if (use && !shaped.has(use)) return;
    const fields = use ? registry.get(use.use)!.hud?.(use.config) ?? [] : [];
    if (!fields.some(f => f.id === ref.field)) fail(`hud[${i}]`, `no HUD field ${ref.from}.${ref.field}`);
  });

  return errors.length ? { ok: false, errors } : { ok: true, spec };
}
